import React, { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import useStyles from './styles'

function List({
  cols,
  listData,
  hideHeadlineColumns,
  SubHeadlineRow,
  itemUpdatingId,
  itemDeletingId,
  Skeleton,
  rowTemplate,
  isBigRow,
  elementType = 'li'
}) {
  // allow using diff for rows to avoid conflicts with other components, and warnings about nesting li in li
  // todo replace all to div
  const RowElement = elementType
  const rowClassName = rowTemplate?.rowClassName
  const rowHasMinHeight = !rowTemplate?.skipMinHeight
  const classes = useStyles({ isBigRow, rowHasMinHeight })

  const HeadLineColumns = useCallback(() => {
    return cols.map(({ headClassName, headTitle, style }) => {
      return (
        <div key={uuidv4()} className={classnames(classes.col, headClassName)} style={style}>
          {headTitle}
        </div>
      )
    })
  }, [cols, classes])

  const Col = useCallback(
    ({ colData, rowData, rowIndex }) => (
      <div className={classnames(classes.col, colData.className)} style={colData.style}>
        {/* return either simple field value or column component */}
        {colData.field ? rowData[colData.field] || '-' : colData.Component(rowData, rowIndex)}
      </div>
    ),
    [classes]
  )

  const DataRows = useCallback(() => {
    return listData.map((rowData, rowIndex) => {
      const isRowUpdating =
        (itemUpdatingId && itemUpdatingId === rowData.id) || (itemDeletingId && itemDeletingId === rowData.id)

      // if rowData has customCols prop, render only those columns
      const colsToRender = rowData.customCols || cols
      const getRowClassName = rowTemplate?.getRowClassName
      const dynamicRowClassName = getRowClassName && getRowClassName(rowData)

      return isRowUpdating ? (
        Skeleton
      ) : (
        <RowElement className={classnames(classes.row, rowClassName, dynamicRowClassName)} key={uuidv4()}>
          {colsToRender.map(col => (
            <Col colData={col} rowData={rowData} key={uuidv4()} rowIndex={rowIndex} />
          ))}
        </RowElement>
      )
    })
  }, [
    listData,
    itemUpdatingId,
    itemDeletingId,
    cols,
    rowTemplate?.getRowClassName,
    Skeleton,
    RowElement,
    classes.row,
    rowClassName,
    Col
  ])

  const HeadlineElement = elementType

  return (
    <>
      {/* first row is headline with columns */}
      {!hideHeadlineColumns && (
        <HeadlineElement className={classnames(classes.row, classes.headlineRow, rowClassName)} key={uuidv4()}>
          <HeadLineColumns />
        </HeadlineElement>
      )}
      {/*In some cases we need to additional row below headline row. E.g. this is used in LineItemsBreakdown component*/}
      {SubHeadlineRow && SubHeadlineRow}
      <DataRows />
    </>
  )
}

const colsPropTypeShape = {
  headTitle: PropTypes.string,
  Component: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  field: PropTypes.string,
  headClassName: PropTypes.string,
  className: PropTypes.string
}

List.propTypes = {
  listData: PropTypes.array.isRequired,
  cols: PropTypes.arrayOf(PropTypes.shape(colsPropTypeShape)).isRequired,
  hideHeadlineColumns: PropTypes.bool,
  SubHeadlineRow: PropTypes.node,
  itemUpdatingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  itemDeletingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowTemplate: PropTypes.shape({
    rowClassName: PropTypes.string,
    getRowClassName: PropTypes.func,
    skipMinHeight: PropTypes.bool
  }),
  isBigRow: PropTypes.bool,
  elementType: PropTypes.oneOf(['li', 'div'])
}

export default List
